import { apiClient } from '@consentropy/shared';
import { API_PATHS } from '@consentropy/shared';
import { mapTimestamps } from '@consentropy/shared';
import type { ApiListResponse, ApiItemResponse } from '@consentropy/shared';
import type { Project, CreateProjectInput, UpdateProjectInput } from '@consentropy/shared';

type BackendProject = Omit<Project, 'createdAt' | 'updatedAt' | 'companyId'> & {
  companyId?: string;
  clientId?: string;
  created: string;
  lastModified: string;
};

type BackendProjectUpdate = Partial<BackendProject>;
type BackendProjectUpdateResponse = BackendProjectUpdate | {
  Attributes: BackendProjectUpdate;
};

const UPDATED_PROJECTS_KEY = 'consentropy.updatedProjects';
const UPDATED_PROJECTS_TTL_MS = 10 * 60 * 1000;

type PersistedUpdatedProject = {
  project: Project;
  updatedAt: number;
};

function getStorage(): Storage | null {
  try {
    return globalThis.sessionStorage;
  } catch {
    return null;
  }
}

function readUpdatedProjects(): Record<string, PersistedUpdatedProject> {
  const storage = getStorage();
  if (!storage) return {};

  const raw = storage.getItem(UPDATED_PROJECTS_KEY);
  if (!raw) return {};

  try {
    const parsed = JSON.parse(raw) as Record<string, PersistedUpdatedProject>;
    const now = Date.now();
    const active = Object.fromEntries(
      Object.entries(parsed).filter(([, value]) => now - value.updatedAt < UPDATED_PROJECTS_TTL_MS),
    ) as Record<string, PersistedUpdatedProject>;

    if (Object.keys(active).length !== Object.keys(parsed).length) {
      writeUpdatedProjects(active);
    }

    return active;
  } catch {
    try {
      storage.removeItem(UPDATED_PROJECTS_KEY);
    } catch {
      // Ignore storage cleanup failures for this fallback layer.
    }
    return {};
  }
}

function writeUpdatedProjects(projects: Record<string, PersistedUpdatedProject>): void {
  const storage = getStorage();
  if (!storage) return;

  try {
    if (Object.keys(projects).length === 0) {
      storage.removeItem(UPDATED_PROJECTS_KEY);
      return;
    }

    storage.setItem(UPDATED_PROJECTS_KEY, JSON.stringify(projects));
  } catch {
    // This fallback should never break the main data flow if storage is unavailable.
  }
}

function normalizeProject(project: BackendProject): Project {
  const mapped = mapTimestamps(project);

  return {
    ...mapped,
    companyId: mapped.companyId ?? mapped.clientId ?? '',
    clientId: mapped.clientId,
  };
}

function normalizeProjects(projects: BackendProject[]): Project[] {
  return projects.map(normalizeProject);
}

function normalizeProjectUpdate(responseItem: BackendProjectUpdateResponse): Project {
  const update = 'Attributes' in responseItem ? responseItem.Attributes : responseItem;
  const mapped = mapTimestamps(update);
  const companyId = mapped.companyId ?? mapped.clientId;

  return {
    ...mapped,
    ...(companyId ? { companyId } : {}),
  } as Project;
}

export function saveUpdatedProject(project: Project): void {
  const updatedProjects = readUpdatedProjects();
  updatedProjects[project.id] = { project, updatedAt: Date.now() };
  writeUpdatedProjects(updatedProjects);
}

function mergeUpdatedProjects(projects: Project[], companyId?: string): Project[] {
  const updatedProjects = readUpdatedProjects();
  let changed = false;
  const merged = new Map(projects.map((project) => [project.id, project]));

  for (const [projectId, value] of Object.entries(updatedProjects)) {
    const updatedProject = value.project;
    const fetchedProject = merged.get(projectId);

    if (!fetchedProject || (companyId && updatedProject.companyId !== companyId)) {
      continue;
    }

    if (JSON.stringify(fetchedProject) === JSON.stringify(updatedProject)) {
      delete updatedProjects[projectId];
      changed = true;
      continue;
    }

    merged.set(projectId, updatedProject);
  }

  if (changed) {
    writeUpdatedProjects(updatedProjects);
  }

  return Array.from(merged.values());
}

function mergeUpdatedProject(project: Project, companyId?: string): Project {
  const updatedProject = readUpdatedProjects()[project.id]?.project;
  if (!updatedProject || (companyId && updatedProject.companyId !== companyId)) {
    return project;
  }

  return updatedProject;
}

function projectClientQuery(companyId: string): string {
  return `companyId=${encodeURIComponent(companyId)}`;
}

export async function getProjects(): Promise<Project[]> {
  const response = await apiClient.get<ApiListResponse<BackendProject>>(API_PATHS.projects);
  return mergeUpdatedProjects(normalizeProjects(response.items));
}

export async function getProjectsByClient(companyId: string): Promise<Project[]> {
  const response = await apiClient.get<ApiListResponse<BackendProject>>(
    `${API_PATHS.projects}?${projectClientQuery(companyId)}`,
  );
  return mergeUpdatedProjects(normalizeProjects(response.items), companyId);
}

export async function getProjectById(id: string, companyId?: string): Promise<Project> {
  const response = await apiClient.get<ApiItemResponse<BackendProject>>(
    `${API_PATHS.projects}${id}${companyId ? `?${projectClientQuery(companyId)}` : ''}`,
  );
  return mergeUpdatedProject(normalizeProject(response.item), companyId);
}

export async function createProject(input: CreateProjectInput): Promise<Project> {
  const response = await apiClient.post<ApiItemResponse<BackendProject>>(
    API_PATHS.projects,
    input,
  );
  return normalizeProject(response.item);
}

export async function updateProject(id: string, input: UpdateProjectInput): Promise<Project> {
  const response = await apiClient.patch<ApiItemResponse<BackendProjectUpdateResponse>>(
    `${API_PATHS.projects}${id}`,
    input,
  );
  return normalizeProjectUpdate(response.item);
}

export async function deleteProject(id: string, companyId?: string): Promise<void> {
  await apiClient.delete(`${API_PATHS.projects}${id}${companyId ? `?${projectClientQuery(companyId)}` : ''}`);
}
